import type { Product } from '@sophia-ai/shared-types';
import { getSessionId } from './session';

/**
 * Fire-and-forget lead-intent signal: the customer clicked a product card
 * through to its Sophia page. Uses `navigator.sendBeacon` so it survives the
 * page navigating away; falls back to a keepalive fetch. Sent as a `text/plain`
 * blob to stay a CORS-simple request (no preflight) — the API reads the raw
 * body and parses it as JSON regardless of content type. Never throws.
 */
export function reportClickthrough(apiBaseUrl: string, product: Product): void {
  const url = `${apiBaseUrl.replace(/\/+$/, '')}/api/clickthrough`;
  const payload = JSON.stringify({
    sessionId: getSessionId(),
    productId: product.id,
    permalink: product.permalink,
    productName: product.name,
  });

  try {
    const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });

    if (navigator.sendBeacon(url, blob)) {
      return;
    }
  } catch {
    // sendBeacon unavailable or threw — fall through to fetch.
  }

  void fetch(url, { method: 'POST', body: payload, keepalive: true }).catch(() => {
    // Best-effort analytics: a dropped beacon must never disrupt the customer.
  });
}
